'use client'

import { GRADE_BANDS } from '@/lib/radar/scoring'

/**
 * The F-A grade scale bar with a position marker.
 *
 * Conventional low-to-high axis: 0/F at the left, 100/A at the right. The
 * marker is placed directly at `left: score%` — a 59 sits at 59% of the bar.
 * No inversion, no per-grade tuning, no hand-placed offsets.
 *
 * Every piece of geometry derives from GRADE_BANDS, the same table
 * `gradeFromScore` grades from: each band paints a hard-stop color segment
 * spanning its true score range, its letter sits at that segment's midpoint,
 * and the accessible description reads the axis endpoints off the table. The
 * bands are uneven (C spans 50-69, F spans 0-29), so the segments are
 * proportional rather than evenly spaced — that proportionality is what
 * guarantees the marker, the letter under it, and the color behind it always
 * name the same grade.
 *
 * A null score renders the scale with NO marker and an explicit caption. A
 * withheld run has no position on this axis, and inventing one would be a
 * misleading picture of a measurement that was never taken.
 */

/** Hard-stop segments: each band's color spans exactly its score range. */
const TRACK = `linear-gradient(90deg, ${GRADE_BANDS.map(
  b => `${b.color} ${b.min}% ${b.max}%`
).join(', ')})`

const AXIS = `on a scale from ${GRADE_BANDS[0].grade} at the left to ${
  GRADE_BANDS[GRADE_BANDS.length - 1].grade
} at the right`

export function markerPosition(score: number): number {
  return Math.max(0, Math.min(100, score))
}

export function GradeScale({
  score,
  /** Accessible description of what the marker represents. */
  label = 'AI readiness score',
}: {
  score: number | null
  label?: string
}) {
  const hasScore = score !== null

  return (
    <div className='flex flex-col gap-2'>
      <div
        className='relative h-2 w-full'
        style={{ background: TRACK }}
        role='img'
        aria-label={
          hasScore
            ? `${label}: ${score} out of 100, ${AXIS}`
            : `${label}: not scored`
        }
      >
        {hasScore && (
          <div
            aria-hidden='true'
            className='absolute top-[-4px] h-4 w-[2px] bg-white'
            style={{
              left: `${markerPosition(score)}%`,
              // Centre the 2px marker on its position.
              transform: 'translateX(-50%)',
            }}
          />
        )}
        {!hasScore && (
          <div
            aria-hidden='true'
            className='absolute inset-0 bg-surface-card/70'
          />
        )}
      </div>
      <div className='relative h-[1.2em]'>
        {GRADE_BANDS.map(b => (
          <span
            key={b.grade}
            aria-hidden='true'
            className='instrument-label absolute leading-none'
            style={{
              left: `${(b.min + b.max) / 2}%`,
              transform: 'translateX(-50%)',
              color: hasScore ? b.color : 'var(--pm-text-muted)',
            }}
          >
            {b.grade}
          </span>
        ))}
      </div>
      {!hasScore && (
        <span className='block text-xs text-instrument'>
          Not enough gradeable data to place a score on this scale.
        </span>
      )}
    </div>
  )
}
